/**
 * free-gift.js
 *
 * Automatically adds the free gift to the cart when eligibility conditions are met.
 * No button click required — the gift is added silently on page load.
 * 
 * Eligibility is determined server-side by Liquid in snippets/free-gift-offer.liquid.
 * If the Liquid conditions are satisfied (feature enabled, gift product configured,
 * cart non-empty, gift not already in cart, customer is first-time/guest without the
 * 'received-free-gift' tag), a hidden sentinel <div> is rendered in the cart DOM.
 * This script finds that sentinel and fires the add.
 *
 * Suppression layers (preventing re-adds after the gift has been added):
 *   sessionStorage 'freeGiftAutoAdded' — within the current browser session for ALL users.
 *     Prevents re-adding if the customer removes the gift and the cart re-renders.
 *   localStorage 'freeGiftAdded' — persists across sessions for GUEST users only.
 *     Logged-in users are suppressed across sessions by Liquid (orders_count check).
 *
 * Concurrent add protection:
 *   The drawer and /cart page both render the sentinel, so two could be found at once.
 *   The module-level addInProgress flag ensures only one fetch fires at a time.
 *
 * Cart re-render after add:
 *   publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' }) triggers onCartUpdate()
 *   on both CartItems (/cart page) and CartDrawerItems (drawer). Fresh section HTML from
 *   Shopify omits the sentinel because gift_in_cart resolves to true.
 *
 * Dependencies (theme globals): routes, publish, PUB_SUB_EVENTS
 */

(function () {
  'use strict';

  var LOG = '[free-gift]';
  var LS_KEY = 'freeGiftAdded';      // localStorage  — cross-session suppression (guests)
  var SS_KEY = 'freeGiftAutoAdded';  // sessionStorage — within-session suppression (all)

  var addInProgress = false; // guards against concurrent adds from drawer + cart page sentinels
  var patchTimer = null;

  /**
   * Reads the unconditionally-rendered data-free-gift-debug element and logs which
   * Liquid conditions and JS suppression flags are blocking (or allowing) the offer.
   * Call this whenever sentinels found: 0 to diagnose the root cause.
   */
  function logDebugState() {
    var debug = document.querySelector('[data-free-gift-debug]');
    if (!debug) {
      console.warn(LOG, 'debug element not found — free-gift-offer snippet may not be included in this cart context');
      return;
    }
    var d = debug.dataset;
    var ls = localStorage.getItem(LS_KEY);
    var ss = sessionStorage.getItem(SS_KEY);

    console.log(LOG, 'Liquid conditions:', {
      'free_gift_enabled': d.enabled,
      'product selected': d.productSet,
      'cart empty': d.cartEmpty,
      'gift already in cart': d.giftInCart,
      'customer eligible': d.eligible,
      'orders_count': d.ordersCount,
    });
    console.log(LOG, 'JS suppression flags:', {
      ['localStorage "' + LS_KEY + '"']: ls || '(not set)',
      ['sessionStorage "' + SS_KEY + '"']: ss || '(not set)',
    });

    if (d.enabled !== 'true')    console.warn(LOG, '→ BLOCKED (Liquid): free_gift_enabled is off — check Theme Settings → Free Gift');
    if (d.productSet !== 'true') console.warn(LOG, '→ BLOCKED (Liquid): no gift product selected — reselect the product in Theme Settings → Free Gift');
    if (d.cartEmpty === 'true')  console.warn(LOG, '→ BLOCKED (Liquid): cart is empty');
    if (d.giftInCart === 'true') console.warn(LOG, '→ BLOCKED (Liquid): gift product is already in the cart');
    if (d.eligible !== 'true')   console.warn(LOG, '→ BLOCKED (Liquid): customer not eligible — logged in with prior orders or has received-free-gift tag (orders_count: ' + d.ordersCount + ')');
    if (ss) console.warn(LOG, '→ SUPPRESSED (JS): sessionStorage "' + SS_KEY + '" is set — open a new tab or clear it in DevTools → Application → Session Storage');
    if (ls) console.warn(LOG, '→ SUPPRESSED (JS): localStorage "' + LS_KEY + '" is set — clear it in DevTools → Application → Local Storage to re-test as a guest');
  }

  /**
   * Recursively searches document and all shadow roots for elements matching selector.
   * Required because the ReCharge storefront drawer renders inside a shadow DOM, making
   * standard document.querySelectorAll blind to its contents.
   */
  function queryAllIncludingShadow(selector, root) {
    root = root || document;
    var found = Array.prototype.slice.call(root.querySelectorAll(selector));
    var allEls = root.querySelectorAll('*');
    for (var i = 0; i < allEls.length; i++) {
      if (allEls[i].shadowRoot) {
        found = found.concat(queryAllIncludingShadow(selector, allEls[i].shadowRoot));
      }
    }
    return found;
  }

  /**
   * Finds the free gift cart item in the ReCharge storefront drawer and injects the
   * compare-at price display + "Free Gift" badge. ReCharge only shows the savings block
   * when original_price > final_price at the line-item level; a variant compare_at_price
   * alone is invisible to it, so we inject the display ourselves.
   *
   * Idempotent: skips wrappers already marked data-free-gift-patched.
   * Runs on a 1s interval (set in observeCartContainers) because shadow DOM mutations
   * are invisible to outside MutationObservers.
   */
  function patchFreeGiftDisplay() {
    var debug = document.querySelector('[data-free-gift-debug]');
    if (!debug) return;

    var giftTitle = debug.dataset.freeGiftProductTitle;
    if (!giftTitle) return;

    var compareAtCents = parseInt(debug.dataset.freeGiftCompareAt || '0', 10);

    var listItems = queryAllIncludingShadow('div[role="listitem"]');
    listItems.forEach(function (item) {
      var titleEl = item.querySelector('p.font-semibold');
      if (!titleEl || titleEl.textContent.trim() !== giftTitle) return;

      var priceWrapper = item.querySelector('div.text-right.whitespace-nowrap');
      if (!priceWrapper || priceWrapper.dataset.freeGiftPatched) return;
      priceWrapper.dataset.freeGiftPatched = 'true';

      if (compareAtCents > 0) {
        var compareAtFormatted = '$' + (compareAtCents / 100).toFixed(2);
        priceWrapper.innerHTML =
          '<span class="sr-only">Original price: ' + compareAtFormatted + '</span>' +
          '<span part="money" class="text-storefront-drawer-muted line-through mr-2">' + compareAtFormatted + '</span>' +
          '<span class="sr-only">Price after discount: $0.00</span>' +
          '<span part="money discount" class="text-storefront-drawer-accent font-semibold">$0.00</span>' +
          '<div class="text-xs text-right text-storefront-drawer-accent font-semibold">Free Gift</div>';
      } else {
        var badge = document.createElement('div');
        badge.className = 'text-xs text-right text-storefront-drawer-accent font-semibold';
        badge.textContent = 'Free Gift';
        priceWrapper.appendChild(badge);
      }
    });
  }

  function schedulePatch() {
    if (patchTimer) clearTimeout(patchTimer);
    patchTimer = setTimeout(function () { patchFreeGiftDisplay(); patchBundlePricing(); }, 200);
  }

  /**
   * Patches ReCharge storefront cart drawer line items for bundle products so that
   * compare_at_price × qty is shown as the strikethrough, and saving % is calculated
   * relative to compare_at_price.
   *
   * Products come from #bundle-pricing-data[data-products] (Logic, Turmeric, …).
   *
   * Surgical approach — ReCharge's [part="money discount"] node is left in place so
   * ReCharge can continue updating the final price on qty changes. We only modify the
   * strikethrough value and add/update a saving badge.
   *
   *   Subscription: [part="money"] is ReCharge's pre-sub strikethrough. We update its
   *     text to compare_at × qty. [part="money discount"] stays owned by ReCharge.
   *   One-time: no strikethrough exists. We insert a .bundle-compare-at span before
   *     [part="money"] (the final price). No part attribute so selector stays unambiguous.
   *
   * Cache key is "qty:finalPriceCents" — re-patches whenever either changes.
   */
  function patchBundlePricing() {
    var sentinel = document.getElementById('bundle-pricing-data');
    if (!sentinel) return;

    // Build title → variantMap. Prefer JSON script body (avoids HTML attr quote issues
    // with apostrophes in titles). Fall back to legacy data-* attributes.
    var productMap = {};
    try {
      var products = null;
      if (sentinel.tagName === 'SCRIPT' || sentinel.type === 'application/json') {
        products = JSON.parse(sentinel.textContent);
      } else if (sentinel.dataset.products) {
        products = JSON.parse(sentinel.dataset.products);
      }

      if (products && products.length) {
        products.forEach(function (p) {
          if (!p || !p.title || !p.variants) return;
          var variantMap = {};
          p.variants.forEach(function (v) {
            variantMap[String(v.title).toLowerCase()] = {
              compareAt: v.compareAt,
              price: v.price,
            };
          });
          productMap[p.title] = variantMap;
        });
      } else if (sentinel.dataset.productTitle && sentinel.dataset.variants) {
        var legacyVariants = JSON.parse(sentinel.dataset.variants);
        var legacyMap = {};
        legacyVariants.forEach(function (v) {
          legacyMap[String(v.title).toLowerCase()] = {
            compareAt: v.compareAt,
            price: v.price,
          };
        });
        productMap[sentinel.dataset.productTitle] = legacyMap;
      }
    } catch (e) {
      return;
    }

    if (!Object.keys(productMap).length) return;

    var subtotalDeltaCents = 0;
    var listItems = queryAllIncludingShadow('div[role="listitem"]');
    listItems.forEach(function (item) {
      var titleEl = item.querySelector('p.font-semibold');
      if (!titleEl) return;

      var variantMap = productMap[titleEl.textContent.trim()];
      if (!variantMap) return;

      var variantEl = item.querySelector('p.text-sm.text-storefront-drawer-muted');
      if (!variantEl) return;

      var variantData = variantMap[variantEl.textContent.trim().toLowerCase()];
      if (!variantData || !variantData.compareAt) return;

      var qtyInput = item.querySelector('input[name="quantity"]');
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      var compareAtCents = variantData.compareAt * qty;

      // Accumulate for every matched bundle item regardless of patching state
      subtotalDeltaCents += compareAtCents - (variantData.price * qty);

      var priceWrapper = item.querySelector('div.text-right.whitespace-nowrap');
      if (!priceWrapper) return;

      // Subscription: has [part="money discount"]; one-time: only [part="money"]
      var discountSpan = priceWrapper.querySelector('[part="money discount"]');
      var moneySpan = priceWrapper.querySelector('[part="money"]');
      var finalPriceEl = discountSpan || moneySpan;
      if (!finalPriceEl) return;

      var finalPriceCents = Math.round(parseFloat(finalPriceEl.textContent.replace(/[^0-9.]/g, '')) * 100);
      if (!finalPriceCents) return;

      if (compareAtCents <= finalPriceCents) return;

      // Re-patch whenever qty or final price changes
      var patchKey = qty + ':' + finalPriceCents;
      if (priceWrapper.dataset.bundlePricingPatched === patchKey) return;

      var compareAtFormatted = '$' + (compareAtCents / 100).toFixed(2);
      var savingPct = Math.round((compareAtCents - finalPriceCents) / compareAtCents * 100);

      // Hide ReCharge's [part="money"] span so its periodic resets stay invisible.
      // Insert our own compare-at span instead — same pattern for both subscription
      // and one-time. ReCharge keeps its live DOM reference; we keep our visible span.
      if (discountSpan && moneySpan) moneySpan.style.display = 'none';
      var strikeSpan = priceWrapper.querySelector('.bundle-compare-at');
      if (!strikeSpan) {
        strikeSpan = document.createElement('span');
        strikeSpan.className = 'text-storefront-drawer-muted line-through mr-2 bundle-compare-at';
        priceWrapper.insertBefore(strikeSpan, moneySpan || finalPriceEl);
      }
      strikeSpan.textContent = compareAtFormatted;

      // One-time: ReCharge renders [part="money"] as muted/gray. Re-style it as the
      // sale price now that we've added a strikethrough compare-at before it.
      if (!discountSpan && moneySpan) {
        moneySpan.classList.remove('text-storefront-drawer-muted');
        moneySpan.classList.add('text-storefront-drawer-accent', 'font-semibold');
      }

      // Remove ReCharge's native saving badge (subscription "Saving $X") before adding ours
      priceWrapper.querySelectorAll('div.text-xs.text-storefront-drawer-accent:not(.bundle-saving-badge)').forEach(function (el) {
        el.remove();
      });

      // Add or update the saving badge
      var savingDiv = priceWrapper.querySelector('.bundle-saving-badge');
      if (!savingDiv) {
        savingDiv = document.createElement('div');
        savingDiv.className = 'text-xs text-right text-storefront-drawer-accent font-semibold bundle-saving-badge';
        priceWrapper.appendChild(savingDiv);
      }
      savingDiv.textContent = 'Saving ' + savingPct + '%';

      priceWrapper.dataset.bundlePricingPatched = patchKey;
    });

    patchSubtotal(subtotalDeltaCents);
  }

  /**
   * Updates the subtotal strikethrough to reflect compare_at × qty for all bundle items
   * instead of ReCharge's original_price × qty.
   *
   * Stores the previously applied delta so re-runs subtract the old value before adding
   * the new one — handles qty changes and multiple polls correctly.
   */
  function patchSubtotal(deltaCents) {
    if (deltaCents === 0) return;

    // Find [part="money"] with line-through that lives outside any list item
    var candidates = queryAllIncludingShadow('[part="money"]');
    var strikeEl = null;
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.closest('[role="listitem"]')) continue;
      if (el.classList.contains('line-through')) { strikeEl = el; break; }
    }
    if (!strikeEl) return;

    var currentCents = Math.round(parseFloat(strikeEl.textContent.replace(/[^0-9.]/g, '')) * 100);
    var lastSetCents = parseInt(strikeEl.dataset.subtotalLastSet || '0', 10);

    // If textContent matches what we last wrote, recover the stored base.
    // If it differs, ReCharge has reset the value — treat current as the new base.
    var baseCents = (lastSetCents > 0 && currentCents === lastSetCents)
      ? parseInt(strikeEl.dataset.subtotalBase || currentCents, 10)
      : currentCents;

    var newCents = baseCents + deltaCents;
    if (currentCents === newCents) return; // already correct

    strikeEl.textContent = '$' + (newCents / 100).toFixed(2);
    strikeEl.dataset.subtotalLastSet = newCents;
    strikeEl.dataset.subtotalBase = baseCents;
  }

  /**
   * Scans for hidden sentinel elements rendered by free-gift-offer.liquid.
   * Skips suppressed sessions, then fires autoAddGift() for the first eligible sentinel.
   * Safe to call multiple times — already-bound sentinels are skipped.
   */
  function initFreeGiftOffer() {
    var sentinels = document.querySelectorAll('[data-free-gift-offer]');
    console.log(LOG, 'initFreeGiftOffer — sentinels found:', sentinels.length);
    if (!sentinels.length) logDebugState();

    sentinels.forEach(function (sentinel) {
      if (sentinel.dataset.freeGiftBound) return;
      sentinel.dataset.freeGiftBound = 'true';

      var isGuest = sentinel.dataset.guest === 'true';
      var variantId = sentinel.dataset.variantId;

      if (!variantId) {
        console.warn(LOG, 'sentinel missing data-variant-id — gift product may not be set in Theme Settings');
        return;
      }

      // Guest with a persistent "already added" flag from a prior browser session
      if (isGuest && localStorage.getItem(LS_KEY)) {
        console.log(LOG, 'localStorage flag set — skipping (guest, prior session)');
        return;
      }

      // All users: skip if the gift was already auto-added this session.
      // This prevents re-adding when the customer removes the gift and the cart re-renders.
      if (sessionStorage.getItem(SS_KEY)) {
        console.log(LOG, 'sessionStorage flag set — skipping (already added this session)');
        return;
      }

      autoAddGift(variantId, isGuest);
    });
  }

  /**
   * POSTs the gift variant to /cart/add.js and triggers a cart re-render on success.
   *
   * @param {string}  variantId - Shopify variant ID (from data-variant-id)
   * @param {boolean} isGuest   - true if the customer is not logged in
   */
  function autoAddGift(variantId, isGuest) {
    if (addInProgress) {
      console.log(LOG, 'add already in progress — skipping concurrent call');
      return;
    }
    addInProgress = true;

    console.log(LOG, 'auto-adding gift — variantId:', variantId, '| isGuest:', isGuest);

    fetch(routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    })
      .then(function (response) {
        console.log(LOG, 'response status:', response.status, response.statusText);
        if (!response.ok) {
          return response.json().then(function (data) {
            console.error(LOG, 'Shopify error response:', data);
            throw new Error((data && data.description) || 'Cart add failed');
          });
        }
        return response.json();
      })
      .then(function (data) {
        console.log(LOG, 'auto-add success —', (data && data.title) || variantId);

        // Lock this session so re-renders don't re-add if the customer removes the gift
        sessionStorage.setItem(SS_KEY, '1');

        // For guests, also lock across future sessions in this browser
        if (isGuest) {
          localStorage.setItem(LS_KEY, '1');
          console.log(LOG, 'localStorage flag set (guest)');
        }

        // Re-render both the drawer and /cart page. CartItems.onCartUpdate() fires for
        // any source except 'cart-items' and 'product-form', so both containers update.
        // The fresh HTML will not include the sentinel (Liquid: gift_in_cart == true).
        console.log(LOG, 'publishing cartUpdate');
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift' });
      })
      .catch(function (err) {
        console.error(LOG, 'auto-add failed:', err.message);
      })
      .finally(function () {
        addInProgress = false;
      });
  }

  /**
   * Observes the stable cart containers for DOM changes and re-runs initFreeGiftOffer.
   * Needed because CartItems.onCartUpdate() / updateQuantity() replace cart DOM nodes —
   * any newly injected sentinel must be picked up and processed.
   *
   * Stable containers:
   *   #CartDrawer  — wraps cart-drawer-items (replaced by onCartUpdate) and .drawer__inner
   *   cart-items   — persists; its innerHTML is mutated by both cart update paths
   */
  function observeCartContainers() {
    var targets = [];

    var drawerContainer = document.getElementById('CartDrawer');
    if (drawerContainer) targets.push(drawerContainer);

    var cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) targets.push(cartItemsEl);

    console.log(LOG, 'observing', targets.length, 'cart container(s)');
    if (!targets.length) return;

    var observer = new MutationObserver(function () {
      initFreeGiftOffer();
    });

    targets.forEach(function (target) {
      observer.observe(target, { childList: true, subtree: true });
    });

    // ReCharge renders inside a shadow DOM — outside MutationObservers can't see its
    // mutations. Poll instead so we re-patch after any ReCharge re-render.
    setInterval(function () { patchFreeGiftDisplay(); patchBundlePricing(); }, 1000);
  }

  document.addEventListener('DOMContentLoaded', function () {
    console.log(
      LOG,
      'DOMContentLoaded — routes.cart_add_url:',
      typeof routes !== 'undefined' ? routes.cart_add_url : 'MISSING (routes not defined)'
    );
    initFreeGiftOffer();
    observeCartContainers();
    // Delay so ReCharge has time to render its cart drawer before we patch.
    setTimeout(function () { patchFreeGiftDisplay(); patchBundlePricing(); }, 400);
  });
})();
